// The persistent "you are inside a client environment" notice (#10718).
//
// Lives in the always-mounted sidebar next to the production-impersonation
// warning, in both the collapsed (icon) and expanded (label + countdown +
// release) renderings, so it is always obvious which client environment is
// active. Renders nothing when the identity payload carries no staff org
// session.
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import { Tooltip } from "@mui/material";
import { ShieldCheck, SignOut } from "@phosphor-icons/react";
import {
  minutesLeft,
  StaffOrgSession,
  useStaffOrgSession,
} from "@/hooks/useStaffOrgSession";
import { CURRENT_USER_QUERY_KEY } from "@/hooks/useCurrentUser";
import {
  clearProductInterval,
  setProductInterval,
} from "@/utils/productTimers";

interface StaffOrgSessionNoticeProps {
  collapsed: boolean;
}

const COUNTDOWN_TICK_MS = 30_000;

function describe(minutes: number): string {
  if (minutes <= 0) return "expired";
  if (minutes < 60) return `${minutes}m left`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours}h left` : `${hours}h ${rest}m left`;
}

const StaffOrgSessionNotice: React.FC<StaffOrgSessionNoticeProps> = ({
  collapsed,
}) => {
  const session = useSelector(
    (state: { userData?: { staffOrgSession?: StaffOrgSession | null } }) =>
      state.userData?.staffOrgSession ?? null
  );
  if (session === null) return null;
  return <ActiveNotice session={session} collapsed={collapsed} />;
};

const ActiveNotice: React.FC<{
  session: StaffOrgSession;
  collapsed: boolean;
}> = ({ session, collapsed }) => {
  const { release } = useStaffOrgSession();
  const queryClient = useQueryClient();
  const [minutes, setMinutes] = useState<number>(() =>
    minutesLeft(session.expiresAt)
  );
  const [releasing, setReleasing] = useState<boolean>(false);
  const [releaseFailed, setReleaseFailed] = useState<boolean>(false);

  // Count down on the viewer's clock; when the session lapses, the server
  // has already reverted the actor, so refetch identity rather than keep
  // claiming an environment that is no longer active.
  useEffect(() => {
    setMinutes(minutesLeft(session.expiresAt));
    const timer = setProductInterval(() => {
      const left = minutesLeft(session.expiresAt);
      setMinutes(left);
      if (left <= 0) {
        void queryClient.invalidateQueries({
          queryKey: CURRENT_USER_QUERY_KEY,
        });
      }
    }, COUNTDOWN_TICK_MS);
    return () => clearProductInterval(timer);
  }, [session.expiresAt, queryClient]);

  const onRelease = async (): Promise<void> => {
    setReleasing(true);
    setReleaseFailed(false);
    const ok = await release();
    setReleasing(false);
    if (!ok) setReleaseFailed(true);
  };

  const label = `In ${session.organizationName}`;
  const remaining = describe(minutes);

  if (collapsed) {
    // Status only: releasing is a deliberate act, so it stays on the
    // expanded rendering where the label makes clear what is being ended.
    return (
      <div
        className="mt-4 flex justify-center"
        role="status"
        aria-label={label}
      >
        <Tooltip title={`${label} · ${remaining}`} arrow placement="right">
          <div className="p-1.5 rounded bg-amber-500">
            <ShieldCheck className="w-4 h-4 text-white" weight="fill" />
          </div>
        </Tooltip>
      </div>
    );
  }

  return (
    <div
      role="status"
      aria-label={label}
      className="mt-2 mx-2 px-2 py-1.5 rounded bg-amber-500 flex items-center gap-2"
    >
      <ShieldCheck className="w-4 h-4 text-white flex-shrink-0" weight="fill" />
      <div className="min-w-0 flex-1 leading-tight">
        <div className="text-xs font-medium text-white truncate">{label}</div>
        <div className="text-[11px] text-amber-50">
          {releaseFailed ? "Release failed" : remaining}
        </div>
      </div>
      <Tooltip title="Release" arrow>
        <button
          type="button"
          onClick={() => void onRelease()}
          disabled={releasing}
          aria-label={`Release ${session.organizationName}`}
          className="p-1 rounded text-white hover:bg-amber-600 disabled:opacity-50"
        >
          <SignOut className="w-4 h-4" />
        </button>
      </Tooltip>
    </div>
  );
};

export default StaffOrgSessionNotice;
